// The preview frame's half of the editor protocol.
//
// This module runs INSIDE the iframe. It composes the page with the same code
// the served document uses (@common/render), writes it into the frame's own
// document, and then adds the three things that make it an editor rather than a
// picture: an element can be highlighted, an element can be clicked to select
// it, and a `data-edit` node can be typed into.
//
// Ported from the archived project's @repoflow/libs/site-mount. What changed:
// the frame is same-origin here, so there is no auth hand-off and no cors list
// — the origin check is the whole of it.
import * as PageRender from "@common/render/page";
import { siteFileBaseOf, FILE_PREFIX } from "@common/schema/site-file/url";

const HIGHLIGHT_STYLE = `
.repoflow-element-highlight { outline: 6px dashed #ffc107; outline-offset: -2px; }
.repoflow-element-over { outline: 4px dashed #bebebe; outline-offset: -2px; }
#repoflow-dimmer { position: fixed; pointer-events: none; }
`;

// The marker the composer writes beside every element, and the token that says
// this frame wrote it.
//
// The machinery behind highlighting and double-click-to-open keys entirely on
// these comments, and before the token a tenant's own markup could carry one:
// `<!-- element 0 -->` is nine characters, and a page's `code` element is a
// textarea. The consequence is not dramatic and it is not nothing — the
// highlight lands on somebody else's node, and double-clicking a section opens
// the wrong element's screen — but the mechanism was "trust anything in the
// document that looks like our own output", which is the part worth removing.
//
// A fresh token per mount, from the CSPRNG where there is one. Stored markup
// cannot carry a live one: the token exists for the length of one mount and is
// passed to the composer as an argument rather than through the render context,
// so no template can read it. See compose() in @common/render/page.
//
// It is not a defence against a script running INSIDE the frame, which can read
// the markers it can see. Nothing here is: that frame is same-origin and P142
// is the row about it.
const ELEMENT_COMMENT = /^\s*element (\d+)(?:\s+([0-9a-f]+))?\s*$/;

const newToken = () => {
  const source = typeof window !== "undefined" && window.crypto && window.crypto.getRandomValues
    ? window.crypto.getRandomValues(new Uint8Array(8))
    : null;

  return source
    ? Array.from(source, (byte) => byte.toString(16).padStart(2, "0")).join("")
    : `${Math.random().toString(16).slice(2)}${Math.random().toString(16).slice(2)}`;
};

// Which `data-edit` attributes this frame is willing to make editable.
//
// The shape `editorMarkup` in @common/render/template rewrites them to, and the
// same prefix `sectionPath` and the page frame's `editablePath` take back off at
// the other end of the message. It is a SHAPE check and not the authority —
// both editors check the path again against the document they hold, because
// only they know which element is which — but it has to be here as well,
// because this is where the DOM decides what a person can type into.
//
// P173: a section's markup is rendered with no editor context (./elements
// passes it for `code` elements only, since a section is shared and editing it
// from a page that carries it would edit every other page that carries it), so
// its `data-edit` attributes keep their bare, section-relative form — `heading`
// rather than `elements[2].config.values.heading`. Wiring every `[data-edit]`
// node regardless made that text look editable, and typing into it sent a path
// the page editor then wrote as a stray top-level key. Unwired, it reads as
// what it is: text this screen does not edit.
const EDITOR_PATH = /^elements\[\d+\]\.config\.values\..+$/;

let options = { highlight: true };
let highlighted = null;
let selected = null;
// Null until the first mount, when the markers in this document start carrying
// one. Read by both walkers below.
let elementToken = null;

// WHERE THE EDITOR IS, told to this document rather than assumed.
//
// Until P142 the frame was a route of the app itself, so `window.location.origin`
// was the editor's origin and naming it as the target was right by accident.
// This document is now served from the sites hostname, so its own origin is the
// one place the editor is NOT — and posting there would send every keystroke to
// nobody while the editor waited.
//
// The editor puts its origin in the frame's URL (`?editor=`), which is the only
// channel a document has before the handshake it is trying to start. It is
// validated as an http(s) origin and nothing else: this frame holds no
// credential and reads no data, so the worst a wrong value achieves is an
// editor that never hears from a frame in somebody else's page.
export const editorOrigin = () => {
  try {
    const declared = new URLSearchParams(window.location.search).get("editor");
    const url = new URL(declared);

    if (url.protocol !== "https:" && url.protocol !== "http:") {
      return window.location.origin;
    }

    return url.origin;
  } catch (e) {
    // Absent or unparseable: the same-origin fallback, which is what a local
    // checkout with no distribution to serve this document from still runs.
    return window.location.origin;
  }
};

export const send = (message, payload) => {
  window.parent.postMessage(
    JSON.stringify({ message, payload }),
    editorOrigin()
  );
};

// Every `<!-- element N -->` in the document, paired with the node it labels.
//
// The node is the next ELEMENT sibling, not `nextSibling.nextSibling` as the
// archived version had it — that assumed exactly one text node between the two
// and silently found the wrong node, or none, whenever the composer's
// whitespace differed.
//
// An element whose template rendered nothing has no node after its comment and
// is skipped: there is nothing to outline.
const eachElement = (visit) => {
  const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_COMMENT);

  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    const match = ELEMENT_COMMENT.exec(node.textContent);

    if (!match || match[2] !== elementToken) {
      continue;
    }

    let sibling = node.nextSibling;
    while (sibling && sibling.nodeType !== Node.ELEMENT_NODE) {
      sibling = sibling.nextSibling;
    }

    if (sibling) {
      visit(parseInt(match[1], 10), sibling);
    }
  }
};

// The dimmer is one fixed box the size of the highlighted element, with a box
// shadow big enough to cover the rest of the page. Cheaper and more accurate
// than four rectangles, and it is what gen-2 did.
const refreshHighlight = () => {
  const dimmer = document.getElementById("repoflow-dimmer");

  if (!dimmer) {
    return;
  }

  if (!options.highlight || !highlighted) {
    dimmer.style.cssText = "position: fixed; pointer-events: none;";
    return;
  }

  const rect = highlighted.getBoundingClientRect();

  dimmer.style.cssText = `
    position: fixed;
    width: ${rect.width}px;
    height: ${rect.height}px;
    top: ${rect.top}px;
    left: ${rect.left}px;
    box-shadow: 0 0 0 ${document.body.offsetHeight}px rgba(0, 0, 0, 0.3);
    pointer-events: none;
  `;
};

export const select = (payload) => {
  const { selected: index, goto } = payload;

  selected = index === null || index === undefined ? null : index;
  highlighted = null;

  eachElement((elementIndex, node) => {
    node.classList.remove("repoflow-element-highlight");
    node.classList.remove("repoflow-element-over");

    if (elementIndex === selected) {
      node.classList.add("repoflow-element-highlight");
      highlighted = node;

      if (goto) {
        // Deferred one tick: the class above changes the outline, which changes
        // the box, and scrolling to the old one lands slightly off.
        setTimeout(
          () => node.scrollIntoView({ behavior: "smooth", block: "nearest" }),
          50
        );
      }
    }
  });

  refreshHighlight();
};

export const config = (payload) => {
  options = { ...options, ...payload };
  refreshHighlight();
};

// Strip what the APPLICATION brought with it.
//
// The frame loads this app — same bundle, same index.html — so it arrives
// carrying bootstrap, font-awesome and the app's own stylesheet. A preview that
// shows the tenant's page on top of the editor's CSS is not a preview: a
// section with no styles of its own would look styled, and every margin would
// be wrong. Nothing here needs them; the route renders no UI.
//
// Returns React's container, which the caller must put BACK. Writing over
// document.body would otherwise detach the node React holds a reference to, and
// a render into a detached container throws — the route renders null today, so
// nothing would notice until the day it does not.
const clearDocument = () => {
  document
    .querySelectorAll('style, link[rel="stylesheet"]')
    .forEach((node) => node.parentNode.removeChild(node));

  const root = document.getElementById("root");

  if (root) {
    root.style.display = "none";
    root.parentNode.removeChild(root);
  }

  return root;
};

// The site's `bodyClasses` and `bodyAttributes`, onto the <body> this frame
// already has.
//
// A served page gets them by having them written into the tag; here the tag
// exists, so they are parsed off a throwaway element and copied. Parsing rather
// than a regex because these are attributes and the browser is the thing that
// knows how to read a list of them — and because what arrives has already been
// checked against the schema's pattern by bodyAttributesOf, so this is a copy
// of something known well-formed rather than a place to be lenient.
//
// A malformed pair yields no attributes rather than throwing: `innerHTML` on a
// detached element cannot navigate, run script or reach this document.
const applyBodyAttributes = (bodyElem, { className, attributes }) => {
  bodyElem.className = className;

  if (!attributes) {
    return;
  }

  const holder = document.createElement("div");
  holder.innerHTML = `<div ${attributes}></div>`;
  const parsed = holder.firstElementChild;

  for (const { name, value } of Array.from(parsed ? parsed.attributes : [])) {
    // `class` is `className` above and would otherwise be applied twice, with
    // the second write silently winning over the field that is actually for it.
    if (name === "class") {
      continue;
    }
    bodyElem.setAttribute(name, value);
  }
};

// The identifying half of the head — <title>, favicon, description and the
// metadata rows — APPLIED to the document this frame already has rather than
// spliced into one it is writing.
//
// A served page gets these by having them written into its `<head>`; this
// document is the editor application's index.html and already carries a title
// and a favicon describing the EDITOR. Appending a second `<title>` would leave
// a document with two of them, the first of which wins in every browser — so
// the value is written to `document.title` and the app's icon link is
// retargeted, which is the same end state a served page reaches.
//
// What is deliberately not applied is charset and viewport: a charset assigned
// after a document has been parsed changes nothing, and a viewport describes a
// viewport this frame does not have. See headDocument in @common/render/page.
//
// Marked with `data-preview-identity` and cleared first, so mounting twice into
// one document — which `mount` does not do today and would be an ordinary thing
// to add — replaces these rows rather than accumulating them.
const applyDocumentIdentity = (identity) => {
  const head = document.getElementsByTagName("head")[0];

  for (const node of document.querySelectorAll("[data-preview-identity]")) {
    node.parentNode.removeChild(node);
  }

  document.title = identity.title;

  // Applied to the frame's own root rather than spliced into its head, for the
  // same reason the title and the icon are: this document already exists and
  // already carries a lang, so the composed page's language REPLACES it. An
  // attribute rather than a node, so it needs no data-preview-identity marker —
  // the next compose overwrites it in place.
  document.documentElement.lang = identity.lang;

  // Retarget the app's own icon rather than adding a second link: two
  // `rel="icon"` links leave which one is used to the browser.
  const icon =
    head.querySelector('link[rel="icon"]') ||
    (identity.favicon ? head.appendChild(document.createElement("link")) : null);

  if (icon) {
    icon.setAttribute("rel", "icon");
    // Emptied rather than removed when the site names no favicon: removing the
    // element would leave the EDITOR's icon describing the tenant's page, and
    // an empty href is the document's own URL. Neither is right, and a link
    // with no href is the one that says "this page names none".
    if (identity.favicon) {
      icon.setAttribute("href", identity.favicon);
    } else {
      icon.removeAttribute("href");
    }
  }

  // One row per identity, REPLACING what this document already says rather than
  // adding to it — the same rule as the title and the icon above, and the case
  // that was missed first time round. The editor's own index.html carries a
  // `<meta name="description">` describing the editor, so appending left two of
  // them with the wrong one first; a reader takes the first.
  //
  // Matched in JS rather than through an attribute selector because `value` is
  // tenant-authored: `meta[property="og:title"]` is fine and `meta[name="a"b"]`
  // is a selector that throws, which would take the mount down over a string
  // somebody typed.
  const meta = (identifier, value, content) => {
    for (const existing of Array.from(head.getElementsByTagName("meta"))) {
      if (existing.getAttribute(identifier) === value) {
        existing.parentNode.removeChild(existing);
      }
    }

    const node = document.createElement("meta");
    node.setAttribute("data-preview-identity", "");
    node.setAttribute(identifier, value);
    node.setAttribute("content", content);
    head.appendChild(node);
  };

  if (identity.description) {
    meta("name", "description", identity.description);
  }

  for (const row of identity.metadata) {
    meta(row.type, row.value, row.content);
  }
};

// A link inside the preview would navigate the frame away from the editor, and
// there is no way back short of reloading the screen.
//
// This was `anchor.onclick = () => false` over `getElementsByTagName("a")` at
// the end of the mount, which covered the anchors that existed AT THAT MOMENT
// and nothing else: not one a section's script appends afterwards, not a
// `<form>`, not an `<area>`. `target="_blank"` and a keyboard activation were
// covered — both dispatch a click — but only on those same anchors.
//
// One listener on the document instead, in the CAPTURE phase so it runs before
// anything the page wired up, and `preventDefault()` rather than
// `stopPropagation()`: the click still reaches the page's own handlers, which
// is deliberate. A single click inside a preview belongs to the page — a tab
// strip, an accordion — and stealing it would make half of what is being
// previewed untestable. What is taken away is only the browser's default
// action, which here is the navigation.
//
// It does not cover `window.open` or a script assigning `location`. Nothing
// inside a same-origin frame can: see the note on the parent's own recovery in
// @repoflow/components/preview, which is what brings the pane back when one of
// those happens.
const preventNavigation = () => {
  document.addEventListener(
    "click",
    (event) => {
      const target = event.target;

      if (target && typeof target.closest === "function" && target.closest("a[href], area[href]")) {
        event.preventDefault();
      }
    },
    true
  );

  // A form posts the frame away exactly as a link does, and a preview of a
  // contact form is a thing people build.
  document.addEventListener("submit", (event) => event.preventDefault(), true);
};

const wireInlineEditing = () => {
  for (const node of document.querySelectorAll("[data-edit]")) {
    const datapath = node.dataset.edit;

    if (!EDITOR_PATH.test(datapath || "")) {
      continue;
    }

    node.contentEditable = "true";

    node.addEventListener("input", (event) => {
      event.stopPropagation();
      send("editor.input", { datapath, value: event.target.innerText.trim() });
    });

    // The frame reloads whenever the page's values change, and a reload while
    // somebody is typing into it destroys the caret. The lock is what stops
    // that: the editor holds the reload until focus leaves.
    node.addEventListener("focus", () => send("editor.lock", { locked: true }));
    node.addEventListener("focusout", () => send("editor.lock", { locked: false }));

    node.addEventListener("mouseover", (event) => {
      event.stopPropagation();
      node.style.outline = "#02d65a dashed 6px";
    });

    node.addEventListener("mouseout", (event) => {
      event.stopPropagation();
      node.style.outline = "";
    });
  }
};

// Which element a click landed in: the nearest `<!-- element N -->` comment
// above it, walking up through ancestors. A click is on the deepest node, and
// nothing on it says which element it belongs to.
const elementIndexAt = (node) => {
  let current = node;

  while (current && current.parentNode) {
    const siblings = Array.from(current.parentNode.childNodes);

    for (let i = siblings.indexOf(current) - 1; i >= 0; i -= 1) {
      if (siblings[i].nodeType === Node.COMMENT_NODE) {
        const match = ELEMENT_COMMENT.exec(siblings[i].textContent);

        // Same token or it is not ours — a comment a tenant typed must not
        // decide which element's screen a double-click opens.
        if (match && match[2] === elementToken) {
          return parseInt(match[1], 10);
        }
      }
    }

    current = current.parentNode;
  }

  return null;
};

export const mount = (payload) => {
  const { page, sections, site, params, data, selected: initial, position } = payload;

  const reactRoot = clearDocument();

  // Minted before the compose and read by both walkers afterwards, so every
  // marker in this document carries it and no marker that arrived any other way
  // does. See ELEMENT_COMMENT.
  elementToken = newToken();

  const { header, body, footer, style, script, config, context } =
    PageRender.compose(
      page,
      sections || {},
      // `site` for the values and attributes a section interpolates, AND for
      // the shell: this composes into a document that already exists, but the
      // document it composes has to be the one a visitor would get.
      { target: "editor", params: params || {}, site, data, editorToken: elementToken }
    );

  const head = document.getElementsByTagName("head")[0];
  const bodyElem = document.getElementsByTagName("body")[0];

  // Same base as the read-only preview beside it. This frame is not sandboxed —
  // it is the live editing surface and needs a real DOM — but it is still on the
  // app's hostname, where a stored file path names no site and resolves to
  // nothing. Composing it differently from the frame next door is what would
  // make the preview stop being one.
  //
  // `site` is optional here — mount() is called with whatever the editor has,
  // and a page can be mounted before its site has resolved. Without one there
  // is no tenant to name, so the base is the identity and a file path in that
  // frame resolves to nothing. That is the honest answer rather than a
  // `/file/by-site///…` built out of two undefineds, and it is what the frame
  // did before this existed.
  const fileBase = site && site.orgId && site.siteId
    ? siteFileBaseOf(site.orgId, site.siteId)
    : FILE_PREFIX;


  // The site's stylesheets, head block and style, from the same code that
  // builds them for a served page — see shell() in @common/render/page. Without
  // them this pane showed the page's markup with none of the site's styling,
  // which on a site whose look comes from what the shell LINKS is not a preview
  // of anything.
  //
  // APPENDED AS A PARSED FRAGMENT, not through `head.innerHTML +=`, and the
  // difference is not tidiness. `innerHTML` never executes a `<script>`, so a
  // script in the site's head block ran on the served page and silently did
  // not here — while `includesScript` and a section's own script, appended
  // below through the same call, did. A preview where one of a site's three
  // script slots is dead is a preview that disagrees with the page it is
  // previewing about which code runs.
  //
  // `+=` also re-serialises and re-parses everything already in this head,
  // replacing every node in it. Nothing depended on those nodes surviving, but
  // the identity applied below does — it holds the document's own <title>.
  //
  // Before HIGHLIGHT_STYLE, so the editor's own outline rules win over a site
  // stylesheet that happens to set `outline`.
  const shell = PageRender.shell(config, style, context);

  // The identifying half FIRST — <title>, favicon, description and the metadata
  // rows, which the frame took none of until now. They are edited on the Page
  // tab, three inches to the left of this pane, and composed only on the
  // read-only screens (P177).
  //
  // First because that is the order a served document has them in: `head()` in
  // @common/render/page emits the identity and then the assets, so a site whose
  // head block carries its own `<link rel="icon">` gets the same answer here as
  // it does there.
  //
  // `fileBase` is the same one the body's file paths are rebased against below,
  // because `faviconUrl` is the one file reference that never passes through
  // `bodyOf`.
  applyDocumentIdentity(PageRender.identityOf(config, context, fileBase));

  const headRange = document.createRange();
  headRange.selectNodeContents(head);
  head.appendChild(headRange.createContextualFragment(shell.head));

  const highlight = document.createElement("style");
  highlight.textContent = HIGHLIGHT_STYLE;
  head.appendChild(highlight);


  // Through the site's body template, so the three streams land inside whatever
  // `<header>`/`<main>`/`<footer>` it wraps them in. It is not decoration: a
  // classless stylesheet styles those tags BY ELEMENT, so a frame that dropped
  // the template rendered the right markup with the wrong layout.
  //
  // The dimmer stays outside it, and the element machinery is unaffected: the
  // `<!-- element N -->` comments sit beside the nodes they label inside these
  // three strings, so they are still siblings wherever the template puts them,
  // and eachElement walks the whole body subtree rather than its top level.
  applyBodyAttributes(bodyElem, PageRender.bodyAttributesOf(config));

  bodyElem.innerHTML = `${PageRender.bodyOf(config, { header, body, footer }, context, fileBase)}<div id="repoflow-dimmer"></div>`;

  // Hidden, and last, so it is out of the way of `body > *` selectors a section
  // may carry. See clearDocument.
  if (reactRoot) {
    bodyElem.appendChild(reactRoot);
  }

  // Scripts have to be re-created to run: innerHTML never executes a <script>.
  // createContextualFragment does, which is what the archived project used and
  // is why the scripts are appended separately from the markup above.
  //
  // The shell's scripts first and the page's own second, the order render()
  // emits them in: a site-wide script is what a page's own is entitled to
  // assume has already run.
  if (shell.scripts) {
    bodyElem.appendChild(
      document.createRange().createContextualFragment(shell.scripts)
    );
  }

  if (script) {
    bodyElem.appendChild(document.createRange().createContextualFragment(script));
  }

  preventNavigation();

  window.addEventListener("scroll", () => {
    send("editor.position", { position: window.scrollY });
    refreshHighlight();
  });

  window.addEventListener("resize", refreshHighlight);

  // Double-click, not click: a single click inside a preview belongs to the
  // page — a tab strip, an accordion — and stealing it would make half of what
  // is being previewed untestable.
  window.addEventListener("dblclick", (event) => {
    const index = elementIndexAt(event.target);

    if (index !== null) {
      send("preview.goto", { index });
    }
  });

  eachElement((index, node) => {
    node.addEventListener("mouseover", () => {
      if (selected === null) {
        node.classList.add("repoflow-element-over");
      }
    });

    node.addEventListener("mouseout", () => {
      node.classList.remove("repoflow-element-over");
    });
  });

  wireInlineEditing();

  // Restore where the frame was before the reload. Without it every keystroke
  // in the markup field scrolls the preview back to the top.
  if (position) {
    window.scrollTo(0, position);
    setTimeout(() => window.scrollTo(0, position), 50);
  }

  select({ selected: initial, goto: !position });
};
